import React, { useEffect, useRef, useState } from 'react';
import { bookingsAPI } from '../../api/bookings';
import { paymentsAPI } from '../../api/payments';
import { useAuth } from '../../hooks/useAuth';
import Textarea from '../common/Textarea';
import Button from '../common/Button';
import PhoneAccountHint from '../common/PhoneAccountHint';
import { ClockIcon, MapPinIcon, CreditCardIcon, DevicePhoneMobileIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatter';
import toast from 'react-hot-toast';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

// Mirror of the backend deposit rules (booking-service lib/deposit.js) —
// preview only; the server-computed values on the created booking are final.
const UG_PUBLIC_HOLIDAYS = ['01-01', '01-26', '02-16', '03-08', '05-01', '06-03', '06-09', '10-09', '12-25', '12-26'];
const previewDepositRate = (dateStr, eventBooking) => {
  if (eventBooking) return 50;
  if (!dateStr) return 30;
  const d = new Date(`${dateStr}T12:00`);
  if (d.getDay() === 0 || d.getDay() === 6) return 50;
  const mmdd = `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return UG_PUBLIC_HOLIDAYS.includes(mmdd) ? 50 : 30;
};

// Generate time slots every 30 min, 7:00 AM – 9:00 PM
const TIME_SLOTS = [];
for (let h = 7; h <= 21; h++) {
  for (let m = 0; m < 60; m += 30) {
    if (h === 21 && m > 0) break;
    const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const ampm = h < 12 ? 'AM' : 'PM';
    TIME_SLOTS.push({ value, label: `${hour12}:${String(m).padStart(2, '0')} ${ampm}` });
  }
}

const BookingForm = ({ provider, service, onSuccess, onCancel }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [serviceType, setServiceType] = useState(
    service.isMobile && service.isInShop ? '' : service.isMobile ? 'mobile' : 'shop'
  );
  const [address, setAddress] = useState({ line1: '', line2: '', city: '' });
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [eventBooking, setEventBooking] = useState(false);
  const submissionKeyRef = useRef(null);

  // Deposit payment step
  const [step, setStep] = useState('details'); // 'details' | 'deposit'
  const [booking, setBooking] = useState(null);
  const [payPhone, setPayPhone] = useState(user?.phone || '');
  const [payMethod, setPayMethod] = useState('MOBILE_MONEY');
  const [payStatus, setPayStatus] = useState(null); // null | PROCESSING | COMPLETED | FAILED
  const [payError, setPayError] = useState('');
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPolling = (paymentId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    const MAX_ATTEMPTS = payMethod === 'CARD' ? 240 : 72;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const p = await paymentsAPI.getPaymentStatus(paymentId);
        if (p.status === 'COMPLETED') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPayStatus('COMPLETED');
          toast.success('Deposit paid — your booking is confirmed!');
          setTimeout(() => onSuccess(), 1200);
          return;
        }
        if (p.status === 'FAILED') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPayStatus('FAILED');
          toast.error(p.failureReason || 'Payment failed. Please try again.');
          return;
        }
      } catch (_) { /* transient — keep polling */ }
      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast('Payment is taking longer than usual — your booking will confirm automatically once it clears.', { icon: '⏳' });
      }
    }, 2500);
  };

  const handlePayDeposit = async () => {
    if (payMethod === 'MOBILE_MONEY' && (!payPhone || !isValidPhoneNumber(payPhone))) {
      setPayError('Enter a valid mobile money number');
      return;
    }
    setPayError('');
    setLoading(true);
    // Optimistic: show the waiting panel immediately; revert on failure
    setPayStatus('PROCESSING');
    try {
      const result = await paymentsAPI.initiatePayment({
        bookingId: booking.id,
        providerId: provider.id,
        amount: booking.depositAmount,
        subtotal: booking.subtotal,
        clientFee: booking.clientFee ?? 2000,
        phoneNumber: payMethod === 'CARD' ? undefined : payPhone,
        method: payMethod,
      });
      setPayStatus('PROCESSING');
      if (result.checkoutUrl) {
        setCheckoutUrl(result.checkoutUrl);
        window.open(result.checkoutUrl, '_blank', 'noopener,noreferrer');
      }
      startPolling(result.paymentId);
    } catch (error) {
      const data = error?.response?.data;
      if (data?.paymentId) {
        // A payment is already in flight for this booking — resume polling it
        setPayStatus('PROCESSING');
        startPolling(data.paymentId);
      } else {
        setPayStatus(null); // revert the optimistic waiting panel
        toast.error(data?.error || 'Could not start the payment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Min date is today
  const today = new Date().toISOString().split('T')[0];

  const handleAddonToggle = (addon) => {
    if (selectedAddons.find(a => a.id === addon.id)) {
      setSelectedAddons(selectedAddons.filter(a => a.id !== addon.id));
    } else {
      setSelectedAddons([...selectedAddons, addon]);
    }
  };

  const calculateTotal = () => {
    const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
    const subtotal = service.price + addonsTotal;
    const clientFee = 2000;
    return { subtotal, clientFee, total: subtotal + clientFee };
  };

  const totalDuration = service.duration + selectedAddons.reduce((sum, a) => sum + (a.durationExtra || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!date) nextErrors.date = 'Please select a date';
    if (!time) nextErrors.time = 'Please select a time';
    if (service.isMobile && service.isInShop && !serviceType) {
      nextErrors.serviceType = 'Please choose where you want the service';
    }

    // Validate not in the past
    const startAt = date && time ? new Date(`${date}T${time}`) : null;
    const minTime = new Date();
    minTime.setHours(minTime.getHours() + 2);
    if (startAt && startAt < minTime) {
      nextErrors.time = 'Please select a time at least 2 hours from now';
    }

    if (serviceType === 'mobile' && (!address.line1 || !address.city)) {
      if (!address.line1) nextErrors.line1 = 'Street address is required for mobile service';
      if (!address.city) nextErrors.city = 'City is required for mobile service';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setLoading(true);
    try {
      submissionKeyRef.current = submissionKeyRef.current || (
        window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`
      );
      const bookingData = {
        idempotencyKey: submissionKeyRef.current,
        clientId: user.id,
        providerId: provider.id,
        serviceId: service.id,
        addonIds: selectedAddons.map(a => a.id),
        startAt: startAt.toISOString(),
        duration: totalDuration,
        subtotal: pricing.subtotal,
        clientFee: pricing.clientFee,
        notes,
      };
      if (serviceType === 'mobile') bookingData.address = address;
      if (eventBooking) bookingData.eventBooking = true;

      const created = await bookingsAPI.createBooking(bookingData);
      submissionKeyRef.current = null;
      setBooking(created);
      setStep('deposit');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error?.response?.data?.error || 'Could not create the booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const pricing = calculateTotal();

  // Provider availability logic
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const availability = provider.availability || [];

  const fmtTime = (t) => {
    const [h, m] = t.split(':');
    const hr = parseInt(h);
    return `${hr === 0 ? 12 : hr > 12 ? hr - 12 : hr}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
  };

  // Get schedule for selected date's day of week
  const daySchedule = date
    ? availability.find(a => a.dayOfWeek === new Date(date + 'T12:00').getDay())
    : null;

  // Filter time slots to only those within provider's working hours (excluding break)
  const availableSlots = daySchedule
    ? TIME_SLOTS.filter(t => {
        if (t.value < daySchedule.startTime || t.value >= daySchedule.endTime) return false;
        // Exclude break times
        if (daySchedule.breakStart && daySchedule.breakEnd) {
          if (t.value >= daySchedule.breakStart && t.value < daySchedule.breakEnd) return false;
        }
        // Exclude past times if date is today
        if (date === today) {
          const now = new Date();
          const minHour = now.getHours() + 2;
          const minMin = now.getMinutes();
          const [h, m] = t.value.split(':').map(Number);
          if (h < minHour || (h === minHour && m < minMin)) return false;
        }
        return true;
      })
    : [];

  const selectStyle = {
    width: '100%', padding: '8px 12px', borderRadius: '6px',
    border: '1px solid var(--gray-6)', backgroundColor: 'var(--color-background)',
    color: 'var(--gray-12)', fontSize: '14px', cursor: 'pointer', outline: 'none',
  };

  const inputStyle = {
    ...selectStyle, cursor: 'text',
  };

  const compactCardStyle = {
    backgroundColor: 'var(--gray-3)',
    border: '1px solid var(--gray-4)',
    borderRadius: '14px',
    padding: 'clamp(12px, 3.5vw, 16px)',
  };

  const compactAccentPanelStyle = {
    backgroundColor: 'var(--orange-2)',
    border: '1px solid var(--orange-4)',
    borderRadius: '14px',
    padding: 'clamp(12px, 3.5vw, 16px)',
  };

  const compactButtonGroupClass = 'flex flex-col gap-2 sm:flex-row sm:justify-center';

  const Label = ({ children, required }) => (
    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '6px', textAlign: 'left' }}>
      {children}
      {required && <span style={{ color: 'var(--red-9, #e5484d)', marginLeft: '3px' }}>*</span>}
    </label>
  );

  const ErrorText = ({ children }) => children ? (
    <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', margin: '6px 0 0', textAlign: 'left' }}>
      {children}
    </p>
  ) : null;

  // ── Step 2: appointment deposit ──
  if (step === 'deposit' && booking) {
    const balance = booking.subtotal - booking.depositAmount;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Deposit summary */}
        <div style={{
          border: '2px solid var(--orange-9)', borderRadius: '16px',
          padding: 'clamp(14px, 4vw, 20px)', textAlign: 'center', background: 'var(--orange-1)',
        }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-9)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>
            Appointment Deposit ({booking.depositRate}%)
          </p>
          <p style={{ fontSize: 'clamp(24px, 7vw, 30px)', fontWeight: 800, color: 'var(--gray-12)', margin: '0 0 4px', fontFamily: "'Poppins', sans-serif" }}>
            {formatCurrency(booking.depositAmount)}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0 }}>
            {booking.depositRate === 50
              ? 'Weekend, holiday & event bookings require a 50% deposit'
              : 'Secures your appointment — deducted from your final bill'}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--gray-11)', margin: '8px 0 0', fontWeight: 600 }}>
            Balance at appointment: {formatCurrency(balance)}
          </p>
        </div>

        {/* Method selector */}
        {payStatus !== 'PROCESSING' && payStatus !== 'COMPLETED' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
              {[
                { id: 'MOBILE_MONEY', label: 'Mobile Money', Icon: DevicePhoneMobileIcon },
                { id: 'CARD', label: 'Card', Icon: CreditCardIcon },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setPayMethod(id)}
                  style={{
                    padding: '10px 8px', borderRadius: '12px', cursor: 'pointer',
                    border: payMethod === id ? '2px solid var(--orange-9)' : '2px solid var(--gray-4)',
                    background: payMethod === id ? 'var(--orange-2)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    fontSize: '12px', fontWeight: 600, color: 'var(--gray-12)',
                  }}
                >
                  <Icon style={{ width: '18px', height: '18px', color: payMethod === id ? 'var(--orange-9)' : 'var(--gray-10)' }} />
                  {label}
                </button>
              ))}
            </div>

            {/* Payment number */}
            {payMethod === 'MOBILE_MONEY' && (
              <div>
                <Label required>Number to pay with</Label>
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="UG"
                  value={payPhone}
                  onChange={(v) => { setPayPhone(v || ''); setPayError(''); }}
                  style={{ width: '100%', borderRadius: '8px', border: '1px solid var(--gray-6)', padding: '8px 12px', backgroundColor: '#fff' }}
                />
                <PhoneAccountHint phone={payPhone} />
                <ErrorText>{payError}</ErrorText>
              </div>
            )}
          </>
        )}

        {/* Waiting for confirmation */}
        {payStatus === 'PROCESSING' && (
          <div style={{ textAlign: 'center', padding: '14px', borderRadius: '12px', background: 'var(--gray-2)' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', margin: '0 auto 10px',
              border: '3px solid var(--orange-9)', borderTopColor: 'transparent',
              animation: 'spin 0.9s linear infinite',
            }} />
            <p style={{ fontSize: '14px', color: 'var(--gray-11)', margin: 0 }}>Waiting for payment confirmation…</p>
            <p style={{ fontSize: '12px', color: 'var(--gray-9)', margin: '4px 0 0' }}>
              {payMethod === 'CARD' ? 'Enter your card details on the payment page' : 'Approve the MoMo prompt on your phone'}
            </p>
            {payMethod === 'CARD' && checkoutUrl && (
              <a
                href={checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  marginTop: '12px', padding: '11px 22px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, var(--orange-9), #f97316)',
                  color: '#fff', fontSize: '13px', fontWeight: 600, textDecoration: 'none',
                }}
              >
                Open card payment page ↗
              </a>
            )}
            <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
          </div>
        )}

        {/* Confirmed */}
        {payStatus === 'COMPLETED' && (
          <div style={{ textAlign: 'center', padding: '16px', borderRadius: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
            <CheckCircleIcon style={{ width: 32, height: 32, color: '#16a34a', margin: '0 auto 8px' }} />
            <p style={{ fontSize: '14px', fontWeight: 600, color: '#15803d', margin: 0 }}>
              Deposit paid — your booking is confirmed!
            </p>
          </div>
        )}

        {/* Actions */}
        {payStatus !== 'COMPLETED' && (
          <div className={compactButtonGroupClass}>
            <Button type="button" variant="outline" size="small" onClick={onCancel} disabled={loading || payStatus === 'PROCESSING'} className="w-full sm:w-auto">
              Pay Later
            </Button>
            {payStatus !== 'PROCESSING' && (
              <Button type="button" size="small" loading={loading} onClick={handlePayDeposit} className="w-full sm:w-auto">
                {payStatus === 'FAILED' ? 'Retry Payment' : `Pay Deposit — ${formatCurrency(booking.depositAmount)}`}
              </Button>
            )}
          </div>
        )}

        <p style={{ fontSize: '11px', color: 'var(--gray-9)', textAlign: 'center', margin: 0 }}>
          Same-day cancellations and no-shows forfeit the deposit. One free reschedule with 24 hours' notice.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

      {/* Service Summary */}
      <div className="rounded-lg p-3 sm:p-4" style={compactCardStyle}>
        <div className="flex items-center justify-between">
          <div>
            <h3 style={{ fontSize: 'clamp(14px, 3.6vw, 15px)', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 4px', textAlign: 'left' }}>{service.name}</h3>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1" style={{ fontSize: '12px', color: 'var(--gray-10)' }}>
                <ClockIcon style={{ width: '14px', height: '14px' }} /> {service.duration} min
              </span>
            </div>
          </div>
          <span style={{ fontSize: 'clamp(16px, 4.2vw, 18px)', fontWeight: 700, color: 'var(--orange-9)', fontFamily: "'Poppins', sans-serif" }}>
            {formatCurrency(service.price)}
          </span>
        </div>
      </div>

      {/* Service Type */}
      {service.isMobile && service.isInShop && (
        <div>
          <Label required>Where do you want the service?</Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            {[
              { value: 'shop', label: 'At Provider Location' },
              { value: 'mobile', label: 'At My Location' },
            ].map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setServiceType(opt.value);
                  setErrors(prev => ({ ...prev, serviceType: '' }));
                }}
                style={{
                  flex: 1, padding: '9px 10px', borderRadius: '10px', cursor: 'pointer',
                  fontSize: '12px', fontWeight: 600, transition: 'all 0.15s',
                  border: serviceType === opt.value ? '2px solid var(--orange-9)' : '1px solid var(--gray-6)',
                  backgroundColor: serviceType === opt.value ? 'var(--orange-2)' : 'transparent',
                  color: serviceType === opt.value ? 'var(--orange-9)' : 'var(--gray-11)',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <ErrorText>{errors.serviceType}</ErrorText>
        </div>
      )}

      {/* Date */}
      <div>
        <Label required>Date</Label>
        <input
          type="date"
          value={date}
          min={today}
          onChange={e => {
            setDate(e.target.value);
            setTime('');
            setErrors(prev => ({ ...prev, date: '', time: '' }));
          }}
          required
          style={inputStyle}
        />
        <ErrorText>{errors.date}</ErrorText>
        {date && !daySchedule && (
          <p style={{ fontSize: '12px', color: 'var(--red-9, #e5484d)', marginTop: '6px', textAlign: 'left' }}>
            Provider is not available on {DAYS[new Date(date + 'T12:00').getDay()]}s
          </p>
        )}
      </div>

      {/* Time */}
      <div>
        <Label required>Time</Label>
        <select
          value={time}
          onChange={e => {
            setTime(e.target.value);
            setErrors(prev => ({ ...prev, time: '' }));
          }}
          required
          disabled={!date || !daySchedule}
          style={{ ...selectStyle, opacity: (!date || !daySchedule) ? 0.5 : 1 }}
        >
          <option value="">
            {!date ? 'Select a date first' : !daySchedule ? 'Not available this day' : 'Select a time'}
          </option>
          {availableSlots.map(t => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <ErrorText>{errors.time}</ErrorText>
        {daySchedule && (
          <p style={{ fontSize: '12px', color: 'var(--gray-9)', marginTop: '4px', textAlign: 'left' }}>
            Working hours: {fmtTime(daySchedule.startTime)} – {fmtTime(daySchedule.endTime)}
            {daySchedule.breakStart && daySchedule.breakEnd && ` (Break: ${fmtTime(daySchedule.breakStart)} – ${fmtTime(daySchedule.breakEnd)})`}
          </p>
        )}
      </div>

      {/* Address (mobile) */}
      {serviceType === 'mobile' && (
        <div className="rounded-lg p-3 sm:p-4" style={compactAccentPanelStyle}>
          <div className="flex items-center gap-2 mb-3">
            <MapPinIcon style={{ width: '15px', height: '15px', color: 'var(--orange-9)' }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--orange-11)' }}>Your Address</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div>
              <Label required>Street Address</Label>
              <input value={address.line1} onChange={e => {
                setAddress({ ...address, line1: e.target.value });
                setErrors(prev => ({ ...prev, line1: '' }));
              }}
                placeholder="123 Main Street" required style={inputStyle} />
              <ErrorText>{errors.line1}</ErrorText>
            </div>
            <div>
              <Label>Apartment / Suite (Optional)</Label>
              <input value={address.line2} onChange={e => setAddress({ ...address, line2: e.target.value })}
                placeholder="Apt 4B" style={inputStyle} />
            </div>
            <div>
              <Label required>City</Label>
              <input value={address.city} onChange={e => {
                setAddress({ ...address, city: e.target.value });
                setErrors(prev => ({ ...prev, city: '' }));
              }}
                placeholder="Kampala" required style={inputStyle} />
              <ErrorText>{errors.city}</ErrorText>
            </div>
            {provider.maxTravelKm && (
              <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>
                Max travel distance: {provider.maxTravelKm} km
              </p>
            )}
          </div>
        </div>
      )}

      {/* Add-ons */}
      {service.addons?.length > 0 && (
        <div>
          <Label>Add-ons (Optional)</Label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {service.addons.map(addon => {
              const selected = !!selectedAddons.find(a => a.id === addon.id);
              return (
                <div
                  key={addon.id}
                  onClick={() => handleAddonToggle(addon)}
                  className="flex items-center justify-between p-3 rounded-lg"
                  style={{
                    cursor: 'pointer', transition: 'all 0.15s',
                    border: selected ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
                    backgroundColor: selected ? 'var(--orange-2)' : 'transparent',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={selected} readOnly
                      style={{ width: '16px', height: '16px', accentColor: 'var(--orange-9)' }} />
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>{addon.name}</p>
                      {addon.description && (
                        <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>{addon.description}</p>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--orange-9)', margin: 0 }}>+{formatCurrency(addon.price)}</p>
                    {addon.durationExtra > 0 && (
                      <p style={{ fontSize: '11px', color: 'var(--gray-9)', margin: 0 }}>+{addon.durationExtra} min</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Event booking flag — 50% deposit per policy */}
      <div
        onClick={() => setEventBooking(!eventBooking)}
        className="flex items-center justify-between p-3 rounded-lg"
        style={{
          cursor: 'pointer',
          border: eventBooking ? '2px solid var(--orange-9)' : '1px solid var(--gray-5)',
          backgroundColor: eventBooking ? 'var(--orange-2)' : 'transparent',
        }}
      >
        <div className="flex items-center gap-3">
          <input type="checkbox" checked={eventBooking} readOnly
            style={{ width: '16px', height: '16px', accentColor: 'var(--orange-9)' }} />
          <div>
            <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
              This is for a wedding, party, or special event
            </p>
            <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>
              Event bookings require a 50% deposit
            </p>
          </div>
        </div>
      </div>

      {/* Notes */}
      <Textarea
        label="Special Requests (Optional)"
        name="notes"
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Any special requests or notes..."
        rows={2}
      />

      {/* Pricing Summary */}
      <div className="rounded-lg p-3 sm:p-4" style={compactCardStyle}>
        <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 10px', textAlign: 'left' }}>Summary</h4>

        <div className="flex justify-between mb-1">
          <span style={{ fontSize: '12px', color: 'var(--gray-11)' }}>Service</span>
          <span style={{ fontSize: '12px', color: 'var(--gray-12)' }}>{formatCurrency(service.price)}</span>
        </div>

        {selectedAddons.map(addon => (
          <div key={addon.id} className="flex justify-between mb-1">
            <span style={{ fontSize: '11px', color: 'var(--gray-10)' }}>+ {addon.name}</span>
            <span style={{ fontSize: '11px', color: 'var(--gray-11)' }}>{formatCurrency(addon.price)}</span>
          </div>
        ))}

        <div className="flex justify-between mb-1" style={{ borderTop: '1px solid var(--gray-4)', paddingTop: '6px', marginTop: '6px' }}>
          <span style={{ fontSize: '12px', color: 'var(--gray-11)' }}>Service Fee</span>
          <span style={{ fontSize: '12px', color: 'var(--gray-11)' }}>{formatCurrency(pricing.clientFee)}</span>
        </div>

        <div className="flex justify-between" style={{ borderTop: '1px solid var(--gray-5)', paddingTop: '8px', marginTop: '6px' }}>
          <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--gray-12)' }}>Total</span>
          <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--orange-9)' }}>{formatCurrency(pricing.total)}</span>
        </div>

        {(() => {
          const rate = previewDepositRate(date, eventBooking);
          const deposit = Math.round((pricing.subtotal * rate) / 100);
          return (
            <div className="flex justify-between" style={{ marginTop: '6px', padding: '8px 10px', borderRadius: '8px', backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-4)' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--orange-11)' }}>
                Deposit due now ({rate}%)
              </span>
              <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--orange-11)' }}>
                {formatCurrency(deposit)}
              </span>
            </div>
          );
        })()}

        <div className="flex items-center gap-1 mt-2">
          <ClockIcon style={{ width: '14px', height: '14px', color: 'var(--gray-9)' }} />
          <span style={{ fontSize: '11px', color: 'var(--gray-10)' }}>Estimated duration: {totalDuration} min</span>
        </div>
      </div>

      {/* Actions */}
      <div className={compactButtonGroupClass}>
        <Button type="button" variant="outline" size="small" onClick={onCancel} disabled={loading} className="w-full sm:w-auto">
          Cancel
        </Button>
        <Button type="submit" size="small" loading={loading} className="w-full sm:w-auto">
          Continue to Deposit
        </Button>
      </div>

      <p style={{ fontSize: '11px', color: 'var(--gray-9)', textAlign: 'center', margin: 0 }}>
        By confirming, you agree to our Terms of Service and Cancellation Policy
      </p>
    </form>
  );
};

export default BookingForm;
